// StudyDost — Homework Print Page
// Route: /teacher/homework/print/:id
// Minimal page — only printable content, auto-triggers print dialog

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://studydost-backend-production.up.railway.app';

export default function HomeworkPrint() {
  const { id } = useParams();
  const [hw, setHw] = useState(null);
  const [error, setError] = useState(null);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    const token = localStorage.getItem('studydost_token');
    fetch(`${API}/api/teacher/homework/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setHw(data);
        // Auto-trigger print only on desktop (blocked on mobile)
        if (!isMobile) setTimeout(() => window.print(), 800);
      })
      .catch(e => setError(e.message));
  }, [id]);

  if (error) return <div style={{ padding: 40, color: 'red' }}>Error: {error}</div>;
  if (!hw) return <div style={{ padding: 40, textAlign: 'center' }}>Loading homework...</div>;

  const q = hw.questions || {};
  const mcq   = q.mcq   || [];
  const short = q.short || [];
  const long  = q.long  || [];

  let qNum = 1;

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }

        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          background: white;
          color: #1F2937;
        }

        .page {
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto;
          background: white;
          padding: 0 0 20px 0;
        }

        /* ── HEADER ── */
        .print-header {
          background: #1565C0;
          color: white;
          padding: 9px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .print-header .logo { font-size: 16px; font-weight: 700; }
        .print-header .tagline { font-size: 9px; opacity: 0.8; margin-top: 1px; }
        .print-header .school-name { font-size: 14px; font-weight: 700; text-align: right; }
        .print-header .whatsapp { font-size: 9px; opacity: 0.8; text-align: right; margin-top: 1px; }

        /* ── META ── */
        .meta-bar {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          border-bottom: 2px solid #1565C0;
          background: #F8FAFC;
        }
        .meta-cell {
          padding: 6px 12px;
          border-right: 1px solid #E2E8F0;
        }
        .meta-cell:last-child { border-right: none; }
        .meta-label { font-size: 8px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.5px; }
        .meta-value { font-size: 11px; font-weight: 600; color: #1F2937; margin-top: 1px; }

        /* ── CHAPTER TITLE ── */
        .chapter-title {
          text-align: center;
          padding: 10px 20px 2px;
          font-size: 20px;
          font-weight: 700;
          color: #1F2937;
        }
        .chapter-sub {
          text-align: center;
          font-size: 10px;
          color: #64748B;
          padding-bottom: 6px;
        }

        /* ── INSTRUCTIONS ── */
        .instructions {
          margin: 0 20px 6px;
          background: #EFF6FF;
          border-radius: 6px;
          padding: 5px 12px;
          font-size: 10.5px;
          color: #374151;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .instructions strong { color: #1565C0; }

        /* ── SECTION HEADER ── */
        .section-header {
          margin: 6px 20px 3px;
          padding: 7px 14px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
          color: white;
          break-after: avoid;
          page-break-after: avoid;
          break-before: avoid;
        }
        .section-mcq   { background: #1E88E5; }
        .section-short { background: #F57C00; }
        .section-long  { background: #7B1FA2; }

        /* ── QUESTION CARD ── */
        .question-card {
          margin: 0 20px 4px;
          padding: 8px 12px;
          border: 1px solid #E2E8F0;
          border-radius: 6px;
          background: #FAFAFA;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .question-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
        }
        .question-text {
          font-size: 11px;
          font-weight: 600;
          line-height: 1.4;
          flex: 1;
        }
        .marks-pill {
          font-size: 10px;
          font-weight: 700;
          padding: 3px 10px;
          border-radius: 12px;
          background: #DBEAFE;
          color: #1565C0;
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* ── MCQ OPTIONS ── */
        .options-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2px 8px;
          margin-top: 6px;
          padding-left: 4px;
        }
        .option {
          font-size: 10.5px;
          color: #374151;
          padding: 2px 0;
          word-break: break-word;
        }
        /* Mobile: single column options */
        @media screen and (max-width: 600px) {
          .options-grid {
            grid-template-columns: 1fr;
          }
          .page {
            width: 100%;
            padding: 0;
          }
          .question-card {
            margin: 0 8px 6px;
          }
          .section-header {
            margin: 8px 8px 4px;
          }
          .print-header {
            padding: 10px 12px;
          }
          .meta-bar {
            grid-template-columns: 1fr 1fr;
          }
          .instructions {
            margin: 0 8px 8px;
            flex-direction: column;
            gap: 4px;
          }
          .chapter-title { font-size: 18px; }
        }

        /* ── FOOTER ── */
        .print-footer {
          margin-top: 16px;
          padding: 10px 20px;
          border-top: 1px solid #E2E8F0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 9px;
          color: #64748B;
        }
        .print-footer strong { color: #1565C0; }

        /* ── PRINT OVERRIDES ── */
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body { width: 210mm; }
          .page { width: 100%; margin: 0; }
          @page { size: A4; margin: 0; }
        }

        /* Screen only */
        @media screen {
          body { background: #f0f4f8; }
          .page {
            margin: 0 auto 20px;
            box-shadow: 0 4px 24px rgba(0,0,0,0.12);
            overflow: hidden;
          }
          .screen-actions {
            position: sticky;
            top: 0;
            z-index: 100;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 16px;
            background: #1565C0;
            gap: 10px;
          }
          .screen-actions .print-btn {
            background: white;
            color: #1565C0;
            border: none;
            padding: 10px 20px;
            font-size: 14px;
            font-weight: 700;
            border-radius: 8px;
            cursor: pointer;
            flex: 1;
            text-align: center;
          }
          .screen-actions .close-btn {
            background: transparent;
            color: white;
            border: 1px solid rgba(255,255,255,0.5);
            padding: 10px 14px;
            font-size: 13px;
            border-radius: 8px;
            cursor: pointer;
            white-space: nowrap;
          }
        }
        @media print { .screen-actions { display: none !important; } }
        .mobile-tip {
          color: rgba(255,255,255,0.85);
          font-size: 10px;
          margin-top: 4px;
          text-align: center;
          width: 100%;
        }
      `}</style>

      {/* Screen-only action bar — sticky so always visible on mobile */}
      <div className="screen-actions">
        <button className="close-btn" onClick={() => window.history.back()}>← Back</button>
        <button className="print-btn" onClick={() => window.print()}>
          🖨️ {isMobile ? 'Save as PDF' : 'Print / Save as PDF'}
        </button>
        {isMobile && (
          <div className="mobile-tip">Tap above → "Save as PDF" in print dialog</div>
        )}
      </div>

      <div className="page">

        {/* Header — branded for free, white-label for paid */}
        <div className="print-header">
          {hw.branded !== false ? (
            <>
              <div>
                <div className="logo">StudyDost.ai</div>
                <div className="tagline">Har Student Ka AI Dost</div>
              </div>
              <div>
                <div className="school-name">{hw.schoolName || 'School'}</div>
                <div className="whatsapp">+91 87963 27556 · studydost.ai</div>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="logo">{hw.schoolName || 'School'}</div>
                <div className="tagline">Academic Year 2025–26</div>
              </div>
              <div>
                <div className="school-name">{hw.subject} · {hw.classLevel}</div>
                <div className="whatsapp">{hw.board} Board</div>
              </div>
            </>
          )}
        </div>

        {/* Meta bar */}
        <div className="meta-bar">
          {[
            ['Teacher', hw.teacherName],
            ['Class', hw.classLevel],
            ['Subject', hw.subject],
            ['Board', hw.board],
          ].map(([label, value]) => (
            <div className="meta-cell" key={label}>
              <div className="meta-label">{label}</div>
              <div className="meta-value">{value || '—'}</div>
            </div>
          ))}
        </div>

        {/* Chapter title */}
        <div className="chapter-title">{hw.chapter}</div>
        <div className="chapter-sub">
          Total Marks: {hw.totalMarks} &nbsp;·&nbsp; {hw.board} Board
        </div>

        {/* Instructions */}
        <div className="instructions">
          <span><strong>Instructions:</strong> Attempt all questions. Write neatly in blue or black ink.</span>
          <strong style={{ color: '#1565C0' }}>Total Marks: {hw.totalMarks}</strong>
        </div>

        {/* MCQ */}
        {mcq.length > 0 && (
          <>
            <div className="section-header section-mcq">
              Section A — Multiple Choice Questions &nbsp;&nbsp; {mcq.length} × 1 = {mcq.length} Marks
            </div>
            {mcq.map((q, i) => (
              <div className="question-card" key={i}>
                <div className="question-top">
                  <div className="question-text">
                    <strong>Q{qNum++}.</strong> {q.q || q.question}
                  </div>
                  <div className="marks-pill">{q.marks || 1} mark</div>
                </div>
                {q.options?.length > 0 && (
                  <div className="options-grid">
                    {q.options.map((opt, j) => (
                      <div className="option" key={j}>{opt}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* Short Answer */}
        {short.length > 0 && (
          <>
            <div className="section-header section-short">
              Section B — Short Answer Questions &nbsp;&nbsp; {short.reduce((a, q) => a + (q.marks || 2), 0)} Marks
            </div>
            {short.map((q, i) => (
              <div className="question-card" key={i}>
                <div className="question-top">
                  <div className="question-text">
                    <strong>Q{qNum++}.</strong> {q.q || q.question}
                  </div>
                  <div className="marks-pill">{q.marks || 2} marks</div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Long Answer */}
        {long.length > 0 && (
          <>
            <div className="section-header section-long">
              Section C — Long Answer Questions &nbsp;&nbsp; {long.reduce((a, q) => a + (q.marks || 5), 0)} Marks
            </div>
            {long.map((q, i) => (
              <div className="question-card" key={i}>
                <div className="question-top">
                  <div className="question-text">
                    <strong>Q{qNum++}.</strong> {q.q || q.question}
                  </div>
                  <div className="marks-pill">{q.marks || 5} marks</div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Footer */}
        <div className="print-footer">
          {hw.branded !== false ? (
            <>
              <span>Generated by <strong>StudyDost.ai</strong></span>
              <strong style={{ color: '#1565C0' }}>
                Ask doubts on WhatsApp: +91 87963 27556
              </strong>
            </>
          ) : (
            <>
              <span>{hw.schoolName}</span>
              <span>Student Name: _________________ &nbsp;&nbsp; Roll No: _______</span>
            </>
          )}
        </div>

      </div>
    </>
  );
}
